import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { useSelection } from '../context/SelectionContext'
import { pobierzAtrybuty } from '../api/falowniki'
import AvailabilityBadge from '../components/AvailabilityBadge'
import TypeChips, { moduleTypes } from '../components/TypeChips'
import BackButton from '../components/BackButton'
import { fetchProducts } from '../requests'
import { formatPrice, formatPricePerWp } from '../utils'
import { KRAJ } from '../kraj'
import i18n from '../i18n'
import useDocumentTitle from '../hooks/useDocumentTitle';

const ENDPOINT = { panel: 'get_panel', inwerter: 'get_inverter', magazyn: 'get_magazine' }
// Teksty z przestrzeni `porownanie`. Funkcje poniżej działają poza komponentem
// (tabela pól), więc biorą tekst z i18n wprost, w chwili renderu.
const tt = (klucz, opcje) => i18n.t(`porownanie:${klucz}`, opcje)
const brak = () => tt('porownanie.brak_danych')

const num = (v) => { const n = typeof v === 'number' ? v : parseFloat(v); return Number.isNaN(n) ? null : n }
const pricePerWpNum = (p) => {
  const d = num(p.price_wp)
  if (d !== null && d > 0) return d
  const nt = num(p.price_netto)
  const pw = num(p.power)
  if (nt !== null && pw !== null && pw > 0) return nt / pw
  return null
}

const txt = (v) => {
  if (v === null || v === undefined) return brak()
  const s = String(v).trim()
  if (s === '' || s.toLowerCase() === 'null') return brak()
  return s
}
// Rodzaj, fazy, napięcie i kolor ramy przychodzą z API jako wartości katalogu
// („1 Fazowy”, „Hybrydowy”). Wartość zostaje bez zmian, tłumaczymy tylko to, co
// widać; nieznana wartość pokazuje się tak, jak przyszła.
const wartosc = (grupa, v) => {
  const s = txt(v)
  return s === brak() ? s : tt(`wartosc.${grupa}.${s}`, { defaultValue: s })
}
const gross = (v) => { const s = formatPrice(v, 0); return s !== null ? `${s} ${KRAJ.symbol}` : brak() }
const netto = (v) => { const s = formatPrice(v, 2); return s !== null ? `${s} ${KRAJ.symbol}` : brak() }
const withUnit = (v, unit) => (v === null || v === undefined || String(v).trim() === '' ? brak() : `${v} ${unit}`)
const warranty = (v) => {
  if (v === null || v === undefined || String(v).trim() === '') return brak()
  const s = String(v).trim()
  return /^\d+$/.test(s) ? tt('porownanie.lat', { count: Number(s), lata: s }) : s
}
const dims = (p) => {
  const parts = [p.module_length, p.module_width, p.module_thickness]
  if (parts.every((x) => x === null || x === undefined || String(x).trim() === '')) return brak()
  return `${parts.map((x) => (x || '—')).join(' × ')} mm`
}
const availabilityCell = (v) =>
  v === null || v === undefined || String(v).trim() === '' ? brak() : <AvailabilityBadge value={v} />
const linkCell = (v) =>
  v ? (
    <a href={v} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
      {tt('porownanie.do_sklepu')}
    </a>
  ) : brak()

// `id` to stały identyfikator wiersza (klucz `pole.<id>` w słowniku), niezależny od języka.
// winner: { dir: 'min'|'max', value: (p)=>number|null, id } — etykieta w `zwyciezca.<id>`
const FIELDS = {
  inwerter: [
    { id: 'cena_brutto', get: (p) => gross(p.price), winner: { dir: 'min', value: (p) => num(p.price), id: 'najnizsza' } },
    { id: 'cena_netto', get: (p) => netto(p.price_netto) },
    { id: 'sprzedawca', get: (p) => txt(p.seller) },
    { id: 'producent', get: (p) => txt(p.manufacturer) },
    { id: 'moc', get: (p) => withUnit(p.power, 'kW') },
    { id: 'typ', get: (p) => wartosc('typ', p.type) },
    { id: 'fazy', get: (p) => wartosc('faza', p.phase) },
    { id: 'mppt', get: (p) => txt(p.mppt) },
    { id: 'dostepnosc', get: (p) => availabilityCell(p.availability) },
    { id: 'wysylka', get: (p) => txt(p.shipping) },
    { id: 'gwarancja', get: (p) => warranty(p.warranty), winner: { dir: 'max', value: (p) => num(p.warranty), id: 'najdluzsza' } },
    { id: 'sklep', get: (p) => linkCell(p.link) },
  ],
  panel: [
    { id: 'cena_brutto', get: (p) => gross(p.price), winner: { dir: 'min', value: (p) => num(p.price), id: 'najnizsza' } },
    { id: 'cena_netto', get: (p) => netto(p.price_netto) },
    { id: 'cena_wp', get: (p) => txt(formatPricePerWp(p)), winner: { dir: 'min', value: (p) => pricePerWpNum(p), id: 'najnizsza_wp' } },
    { id: 'sprzedawca', get: (p) => txt(p.seller) },
    { id: 'producent', get: (p) => txt(p.manufacturer) },
    { id: 'moc', get: (p) => withUnit(p.power, 'W') },
    // Moduł PV może mieć dwa typy — chipy z pola `types` (fallback: `type`).
    { id: 'typ', get: (p) => (moduleTypes(p).length ? <TypeChips types={p.types} type={p.type} /> : brak()) },
    { id: 'sprawnosc', get: (p) => withUnit(p.efficiency, '%'), winner: { dir: 'max', value: (p) => num(p.efficiency), id: 'najwyzsza' } },
    { id: 'gwarancja_produktowa', get: (p) => warranty(p.product_warranty), winner: { dir: 'max', value: (p) => num(p.product_warranty), id: 'najdluzsza' } },
    { id: 'gwarancja_liniowa', get: (p) => warranty(p.eff_warranty) },
    { id: 'wymiary', get: (p) => dims(p) },
    { id: 'waga', get: (p) => withUnit(p.weight, 'kg') },
    { id: 'kolor_ramy', get: (p) => wartosc('kolor_ramy', p.frame_colour) },
    { id: 'dostepnosc', get: (p) => availabilityCell(p.availability) },
    { id: 'wysylka', get: (p) => txt(p.shipping) },
    { id: 'sklep', get: (p) => linkCell(p.link) },
  ],
  magazyn: [
    { id: 'cena_brutto', get: (p) => gross(p.price), winner: { dir: 'min', value: (p) => num(p.price), id: 'najnizsza' } },
    { id: 'cena_netto', get: (p) => netto(p.price_netto) },
    { id: 'sprzedawca', get: (p) => txt(p.seller) },
    { id: 'producent', get: (p) => txt(p.manufacturer) },
    { id: 'pojemnosc', get: (p) => withUnit(p.capacity, 'kWh') },
    { id: 'napiecie', get: (p) => wartosc('napiecie', p.voltage) },
    { id: 'typ', get: (p) => wartosc('typ', p.type) },
    { id: 'dostepnosc', get: (p) => availabilityCell(p.availability) },
    { id: 'wysylka', get: (p) => txt(p.shipping) },
    { id: 'gwarancja', get: (p) => warranty(p.warranty), winner: { dir: 'max', value: (p) => num(p.warranty), id: 'najdluzsza' } },
    { id: 'sklep', get: (p) => linkCell(p.link) },
  ],
}

// Etykieta wiersza: pola techniczne ze słownika, wiersze z badań wprost z API (`etykieta`).
const etykietaPola = (f) => (f.badawczy ? f.label : tt(`pole.${f.id}`))
const etykietaZwyciezcy = (f) => tt(`zwyciezca.${f.winner.id}`, { waluta: KRAJ.symbol })

const Compare = () => {
  const { t } = useTranslation('porownanie')
  useDocumentTitle(t('porownanie.tytul'));
  const { comparisonItems, comparisonCategory, removeComparison } = useSelection()
  const navigate = useNavigate()
  const [freshMap, setFreshMap] = useState({})

  const idsKey = comparisonItems.map((it) => `${it.category}:${it.id}`).join(',')

  useEffect(() => { window.scrollTo(0, 0) }, [])

  // Pobierz świeże dane po ID (fallback: snapshot z localStorage).
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      const entries = await Promise.all(
        comparisonItems.map(async (it) => {
          const key = `${it.category}:${it.id}`
          try {
            const data = await fetchProducts(`${ENDPOINT[it.category]}/${it.id}`)
            if (data && !data.error && data.id != null) return [key, data]
          } catch (e) {
            // ignore -> fallback do snapshotu
          }
          return [key, null]
        })
      )
      if (!cancelled) {
        setFreshMap((prev) => {
          const next = { ...prev }
          entries.forEach(([k, v]) => { if (v) next[k] = v })
          return next
        })
      }
    }
    load()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey])

  // Źródłem prawdy jest lista z contextu (spójny licznik i natychmiastowe usuwanie);
  // wzbogacamy o świeże dane, gdy dotrą.
  const products = useMemo(
    () => comparisonItems.map((it) => ({ ...it, ...(freshMap[`${it.category}:${it.id}`] || {}), category: it.category })),
    [comparisonItems, freshMap]
  )

  const count = products.length
  const fields = FIELDS[comparisonCategory] || []

  /**
   * Atrybuty z danych badawczych — zasilanie awaryjne, serwis, aplikacja, taryfy.
   * Na razie tylko falowniki: dla modułów i magazynów badania jeszcze nie było.
   */
  const [atrybuty, setAtrybuty] = useState({})
  const modelIdsKey = products.map((p) => p.model_id).filter(Boolean).join(',')
  useEffect(() => {
    if (comparisonCategory !== 'inwerter' || !modelIdsKey) { setAtrybuty({}); return undefined }
    let cancelled = false
    pobierzAtrybuty(modelIdsKey.split(',').map(Number)).then((res) => {
      if (!cancelled && res.ok) setAtrybuty(res.data)
    })
    return () => { cancelled = true }
  }, [comparisonCategory, modelIdsKey])

  /**
   * Wiersze z badań doklejone POD parametrami technicznymi, w kolejności
   * ustalonej przez backend (`atrybuty.KOLEJNOSC`) — od tego, co decyduje
   * najczęściej, w dół.
   */
  const wierszeBadawcze = useMemo(() => {
    const kolejnosc = []
    const etykiety = {}
    products.forEach((p) => {
      const lista = atrybuty[String(p.model_id)]?.atrybuty || []
      lista.forEach((a) => {
        if (!(a.pole in etykiety)) { etykiety[a.pole] = a.etykieta; kolejnosc.push(a.pole) }
      })
    })
    return kolejnosc.map((pole) => ({
      id: `badawczy:${pole}`,
      label: etykiety[pole],
      badawczy: true,
      dane: (p) => (atrybuty[String(p.model_id)]?.atrybuty || []).find((a) => a.pole === pole) || null,
    }))
  }, [products, atrybuty])

  const wszystkie = useMemo(() => [...fields, ...wierszeBadawcze], [fields, wierszeBadawcze])

  /** Tekst komórki — służy wyłącznie do sprawdzenia, czy wszystkie są takie same. */
  const tekstKomorki = (f, p) => {
    if (f.badawczy) return f.dane(p)?.wartosc || brak()
    const v = f.get(p)
    return typeof v === 'string' ? v : JSON.stringify(v)
  }

  /**
   * ROZDZIAŁ NA RÓŻNICE I ZGODNOŚCI.
   * Wiersz, w którym wszystkie kolumny mówią to samo, nie niesie żadnej
   * informacji, a zajmuje tyle miejsca co ten, który rozstrzyga wybór. Zwijamy
   * takie wiersze do listy w panelu obok, żeby tabela została na różnicach.
   *
   * Wiersz, w którym NIKT nie ma danych, wypada całkiem: „brak danych" trzy razy
   * obok siebie to nie jest zgodność produktów, tylko nasza dziura.
   */
  const { rozne, zgodne } = useMemo(() => {
    if (count < 2) return { rozne: wszystkie, zgodne: [] }
    const r = []
    const z = []
    wszystkie.forEach((f) => {
      const wartosci = products.map((p) => tekstKomorki(f, p))
      const takieSame = wartosci.every((v) => v === wartosci[0])
      if (takieSame && wartosci[0] === brak()) return
      if (takieSame) z.push({ f, wartosc: wartosci[0] })
      else r.push(f)
    })
    return { rozne: r, zgodne: z }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wszystkie, products, count])

  // Wyróżnienia zwycięzców — tylko przy >= 2 produktach i >= 2 porównywalnych wartościach.
  const winners = useMemo(() => {
    const map = {}
    if (count < 2) return map
    fields.forEach((f) => {
      if (!f.winner) return
      const vals = products.map((p) => f.winner.value(p)).filter((v) => v !== null && v !== undefined)
      if (vals.length < 2) return
      const best = f.winner.dir === 'min' ? Math.min(...vals) : Math.max(...vals)
      const bestCount = vals.filter((v) => v === best).length
      if (bestCount === 1) map[f.id] = best
    })
    return map
  }, [products, fields, count])

  /**
   * Etykieta NAD kolumną mówi, dlaczego ten produkt wygrywa — i tylko wtedy, gdy
   * naprawdę coś wygrywa. Trzy sztywne etykiety zmuszałyby do wymyślania trzeciej,
   * a wymyślona etykieta jest gorsza od pustego miejsca.
   */
  const etykietyKolumn = useMemo(() => {
    const wynik = {}
    if (count < 2) return wynik
    fields.forEach((f) => {
      if (!f.winner || winners[f.id] === undefined) return
      const zwyciezca = products.find((p) => f.winner.value(p) === winners[f.id])
      if (!zwyciezca) return
      const k = `${zwyciezca.category}:${zwyciezca.id}`
      if (wynik[k]) return
      if (f.id === 'cena_brutto') wynik[k] = t('kolumna.najtanszy')
      else if (f.id === 'cena_wp') wynik[k] = t('kolumna.najtanszy_za_wat')
      else if (f.id === 'sprawnosc') wynik[k] = t('kolumna.najwyzsza_sprawnosc')
      else if (f.id.startsWith('gwarancja')) wynik[k] = t('kolumna.najdluzsza_gwarancja')
      else wynik[k] = `${etykietaZwyciezcy(f)} ${etykietaPola(f).toLowerCase()}`
    })
    return wynik
  }, [products, fields, winners, count, t])

  /**
   * Mieszanka hybryd i falowników sieciowych. NIE blokujemy takiego porównania:
   * „czy dopłacić do hybrydy" to jedno z najczęstszych realnych pytań i to jedyne
   * miejsce, gdzie da się na nie odpowiedzieć liczbami. Mówimy tylko wprost, że
   * część wierszy nie dotyczy sieciowego.
   */
  const mieszanka = useMemo(() => {
    if (comparisonCategory !== 'inwerter' || count < 2) return false
    const typy = new Set(products.map((p) => p.type).filter(Boolean))
    return typy.has('Hybrydowy') && [...typy].some((t) => t !== 'Hybrydowy')
  }, [products, comparisonCategory, count])

  const header = (
    <div className="mb-6">
      <div className="mb-4"><BackButton /></div>
      <h1 className="text-2xl font-bold text-gray-900">{t('porownanie.tytul')}</h1>
      {comparisonCategory && (
        <p className="text-sm text-gray-500 mt-1">
          {t('porownanie.kategoria')} <span className="font-medium text-gray-700">{t(`kategoria.${comparisonCategory}`)}</span> · {count}/4
        </p>
      )}
    </div>
  )

  if (count === 0) {
    return (
      <div className="px-4 md:px-10 lg:px-24 mt-8 mb-16">
        {header}
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-lg text-gray-400 mb-5">{t('porownanie.pusto')}</p>
          <Link to="/panele" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition-colors">
            {t('porownanie.przegladaj')}
          </Link>
        </div>
      </div>
    )
  }

  const komorka = (f, p) => {
    if (!f.badawczy) return f.get(p)
    const a = f.dane(p)
    if (!a) return <span className="text-gray-400">{brak()}</span>
    return (
      <>
        <span>{a.wartosc}</span>
        {/* Źródło przy KAŻDEJ wartości z badań. Bez niego nie da się odróżnić
            instrukcji producenta od wpisu na forum, a na tej różnicy stoi
            wiarygodność całej tabeli. */}
        {a.zrodlo_url && (
          <a href={a.zrodlo_url} target="_blank" rel="noopener noreferrer"
             className="block mt-1 text-[10px] text-gray-400 hover:text-blue-600 underline underline-offset-2">
            {/* Poziom źródła to wartość z danych badawczych („karta_katalogowa”);
                po polsku pokazujemy ją jak dotąd, inne języki mają słownik. */}
            {t('porownanie.zrodlo', {
              poziom: a.poziom_zrodla
                ? t(`poziom_zrodla.${a.poziom_zrodla}`, { defaultValue: a.poziom_zrodla.replace(/_/g, ' ') })
                : t('porownanie.zrodlo_link'),
            })}
            {a.zakres === 'producent' ? t('porownanie.dane_o_marce') : ''}
            {a.do_potwierdzenia ? t('porownanie.do_potwierdzenia') : ''}
          </a>
        )}
      </>
    )
  }

  return (
    <div className="px-4 md:px-10 lg:px-24 mt-8 mb-24 md:mb-16">
      {header}

      {count === 1 && (
        <div className="mb-4 rounded-lg bg-blue-50 border border-blue-100 text-blue-700 text-sm px-4 py-3">
          {t('porownanie.dodaj_jeszcze')}
        </div>
      )}

      {mieszanka && (
        <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3">
          {t('porownanie.mieszanka')}
        </div>
      )}

      <div className="grid lg:grid-cols-[260px_minmax(0,1fr)] gap-5 items-start">
        {/* Podsumowanie po LEWEJ, przed tabelą: czyta się od lewej, więc wnioski
            mają stać przed danymi, a nie za nimi. */}
        <aside className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 lg:sticky lg:top-4">
          <h2 className="text-sm font-bold text-gray-900">{t('porownanie.podsumowanie')}</h2>
          {count < 2 ? (
            <p className="mt-2 text-xs text-gray-500">{t('porownanie.dodaj_drugi')}</p>
          ) : (
            <>
              <p className="mt-2 text-xs text-gray-500 leading-5">
                {t('porownanie.roznic')}<strong className="text-gray-700">{rozne.length}</strong>
                {zgodne.length > 0 && (
                  <>{t('porownanie.wspolnych')}<strong className="text-gray-700">{zgodne.length}</strong></>
                )}
              </p>

              {Object.keys(etykietyKolumn).length > 0 && (
                <ul className="mt-3 space-y-2">
                  {products.filter((p) => etykietyKolumn[`${p.category}:${p.id}`]).map((p) => (
                    <li key={`sum:${p.category}:${p.id}`} className="text-xs">
                      <span className="block font-semibold text-blue-700">
                        {etykietyKolumn[`${p.category}:${p.id}`]}
                      </span>
                      <span className="block text-gray-600 line-clamp-2">{p.name}</span>
                    </li>
                  ))}
                </ul>
              )}

              {zgodne.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
                    {t('porownanie.takie_same')}
                  </p>
                  <ul className="space-y-1">
                    {zgodne.map(({ f, wartosc: w }) => (
                      <li key={`z:${f.id}`} className="text-xs text-gray-500">
                        <span className="text-gray-400">{etykietaPola(f)}:</span>{' '}
                        {typeof w === 'string' ? w : '—'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </aside>

        <div className="overflow-x-auto border border-gray-100 rounded-2xl bg-white shadow-sm">
          <table className="table-fixed w-full border-collapse min-w-[680px]">
            <colgroup>
              <col className="w-40 md:w-48" />
              {products.map((p) => (<col key={`col:${p.category}:${p.id}`} />))}
            </colgroup>
            <thead>
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide px-4 py-3 border-b border-gray-100">
                  {t('porownanie.parametr')}
                </th>
                {products.map((p) => (
                  <th key={`${p.category}:${p.id}`} className="align-top px-4 py-3 border-b border-l border-gray-100">
                    <div className="flex flex-col items-center text-center gap-2">
                      {etykietyKolumn[`${p.category}:${p.id}`] && (
                        <span className="px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold uppercase tracking-wide">
                          {etykietyKolumn[`${p.category}:${p.id}`]}
                        </span>
                      )}
                      <div className="relative w-full flex justify-center">
                        <img src={p.img_url} alt={p.name} className="h-20 object-contain" />
                        <button
                          onClick={() => removeComparison(p.id, p.category)}
                          aria-label={t('porownanie.usun')}
                          className="absolute -top-1 right-0 p-1 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                      <Link
                        to={`/${p.category}/${p.id}`}
                        className="text-sm font-semibold text-gray-800 hover:text-blue-600 leading-snug break-words line-clamp-3"
                      >
                        {p.name}
                      </Link>
                      <button
                        onClick={() => removeComparison(p.id, p.category)}
                        className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                      >
                        {t('porownanie.usun')}
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rozne.map((f, ri) => (
                <tr key={f.id} className={ri % 2 ? 'bg-gray-50/40' : ''}>
                  <td className="sticky left-0 z-10 bg-inherit text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 border-b border-gray-100 align-top">
                    {etykietaPola(f)}
                  </td>
                  {products.map((p) => {
                    const winVal = f.winner ? winners[f.id] : undefined
                    const cellVal = f.winner ? f.winner.value(p) : null
                    const isWinner = winVal !== undefined && cellVal !== null && cellVal === winVal
                    const priceWin = isWinner && f.winner && f.winner.id === 'najnizsza'
                    return (
                      <td
                        key={`${p.category}:${p.id}:${f.id}`}
                        className={`px-4 py-3 border-b border-l border-gray-100 text-sm align-top break-words ${
                          isWinner ? 'bg-emerald-50/70' : ''
                        } ${priceWin ? 'text-emerald-700 font-semibold' : 'text-gray-700'}`}
                      >
                        {komorka(f, p)}
                        {isWinner && (
                          <span className="block text-[10px] font-medium text-emerald-600 mt-0.5">{etykietaZwyciezcy(f)}</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6">
        <button onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:underline">
          {t('porownanie.wroc')}
        </button>
      </div>
    </div>
  )
}

export default Compare
